// src/components/widgets/RulModelInsightsWidget.js
// ============================================================================
// FlowSense Enterprise — RUL Model Insights Widget
// Version: 2.0.1-enterprise
// ============================================================================

import React, {
  useState, useEffect, useCallback, memo, useMemo, useRef,
} from 'react';
import {
  Box, Heading, Spinner, Flex, Text, useColorModeValue, SimpleGrid,
  Stat, StatLabel, StatNumber, StatHelpText, VStack, Alert, AlertIcon,
  Button, useToast, HStack, Icon, IconButton, Tooltip, Divider, List,
  ListItem, ListIcon, Tag, Badge, Progress, Collapse, useDisclosure,
  Menu, MenuButton, MenuList, MenuItem, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Table, Thead,
  Tbody, Tr, Th, Td, TableContainer, Input, Select, Tabs, TabList, Tab,
  TabPanels, TabPanel, Code, Switch, FormControl, FormLabel,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, Skeleton, SkeletonText,
  AlertTitle, AlertDescription, Wrap, WrapItem
} from '@chakra-ui/react';
import {
  RepeatIcon, CheckCircleIcon, ArrowUpIcon, WarningIcon,
  DownloadIcon, ChevronDownIcon, ChevronUpIcon, InfoIcon, DeleteIcon,
} from '@chakra-ui/icons';
import {
  FaSync, FaChartBar, FaHourglassHalf, FaCheckCircle,
  FaExclamationTriangle, FaClock, FaRobot, FaHistory,
  FaShieldAlt, FaBrain, FaTachometerAlt, FaDatabase,
} from 'react-icons/fa';
import { MdTrendingUp, MdTrendingDown, MdTrendingFlat } from 'react-icons/md';
import ReactApexChart from 'react-apexcharts';
import { motion, AnimatePresence } from 'framer-motion';
import { keyframes } from '@emotion/react';

import analyticsService from '../../services/analyticsService';
import { triggerRulModelRetraining } from '../../services/predictiveFailureService';
import aiService from '../../services/aiService';
import Card from '../card/Cards';

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1 — KEYFRAMES
// ─────────────────────────────────────────────────────────────────────────────

const livePulseKF = keyframes`
  0%   { transform: scale(0.85); box-shadow: 0 0 0 0 rgba(52,211,153,0.7); }
  70%  { transform: scale(1);    box-shadow: 0 0 0 7px rgba(52,211,153,0); }
  100% { transform: scale(0.85); box-shadow: 0 0 0 0 rgba(52,211,153,0); }
`;

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 2 — ANIMATION VARIANTS (Framer Motion)
// ─────────────────────────────────────────────────────────────────────────────

const FADE_UP = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0,  transition: { duration: 0.26, ease: 'easeOut' } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.16 } },
};

const STAGGER_CONTAINER = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const LIST_ITEM_VARIANT = {
  hidden:  { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0,   transition: { duration: 0.22, ease: 'easeOut' } },
  exit:    { opacity: 0, x: 12,  transition: { duration: 0.14 } },
};

const METRIC_CARD_VARIANT = {
  hidden:  { opacity: 0, y: 14, scale: 0.97 },
  visible: { opacity: 1, y: 0,  scale: 1, transition: { duration: 0.24, ease: 'easeOut' } },
};

const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 3 — METRIC EXTRACTION UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

const deepParseJson = (data, depth = 0) => {
  if (depth > 3) return data; 
  if (typeof data === 'object' && data !== null) return data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return deepParseJson(parsed, depth + 1);
    } catch (_) {
      return data;
    }
  }
  return data;
};

const extractMetric = (model, metric) => {
  if (!model) return null;
  const pm = deepParseJson(model.performance_metrics) || {};

  const candidates = (() => {
    switch (metric) {
      case 'mae':
        return [pm.mae, pm.mean_absolute_error, model.mae, model.mean_absolute_error];
      case 'r2':
        return [pm.r2, pm.r_squared, pm.r2_score, pm.c_index,
                model.r2_score, model.c_index, model.r2, model.r_squared];
      case 'rmse':
        return [pm.rmse, pm.root_mean_squared_error,
                model.rmse, model.root_mean_squared_error];
      case 'mape':
        return [pm.mape, pm.mean_absolute_percentage_error,
                model.mape, model.mean_absolute_percentage_error];
      default:
        return [pm[metric], model[metric]];
    }
  })();

  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null) {
      const n = parseFloat(candidate);
      if (!isNaN(n)) return n;
    }
  }
  return null;
};

const metricDeltaColor = (metric, prodValue, stagedValue) => {
  if (prodValue === null || stagedValue === null) return 'gray';
  const higherIsBetter = ['r2'];
  return higherIsBetter.includes(metric)
    ? (stagedValue > prodValue ? 'green' : 'red')
    : (stagedValue < prodValue ? 'green' : 'red');
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 4 — SHARED UI ATOMS
// ─────────────────────────────────────────────────────────────────────────────

const MetricCard = memo(({
  label, value, helpText, accent = '#3182CE',
  icon: Ic, isHealthy = true, isLoading = false,
}) => {
  const bg             = useColorModeValue('white',    'gray.800');
  const bd             = useColorModeValue('gray.200', 'rgba(255,255,255,0.08)');
  const lbl            = useColorModeValue('gray.500', 'gray.400');
  const iconBg         = useColorModeValue(`${accent}18`, `${accent}22`);
  const unhealthyColor = useColorModeValue('red.500',  'red.300');

  return (
    <MotionBox variants={METRIC_CARD_VARIANT}>
      <Box
        bg={bg}
        borderWidth="1px" borderColor={bd} borderRadius="xl"
        p={4} position="relative" overflow="hidden"
        boxShadow="sm"
        _hover={{ boxShadow: 'md', transform: 'translateY(-2px)', borderColor: accent }}
        transition="all 0.2s ease"
      >
        <Box
          position="absolute" top={0} left={0} right={0} h="3px"
          bg={accent}
          style={{ boxShadow: `0 0 8px ${accent}66` }}
          borderTopRadius="xl"
        />

        {isLoading ? (
          <VStack spacing={2} align="flex-start" pt={1}>
            <Skeleton height="10px" w="60%" />
            <Skeleton height="24px" w="80%" />
            <Skeleton height="8px"  w="40%" />
          </VStack>
        ) : (
          <Flex justify="space-between" align="flex-start" pt={1}>
            <VStack align="flex-start" spacing={1} flex={1} overflow="hidden">
              <Text fontSize="10px" fontWeight="800" color={lbl} textTransform="uppercase" letterSpacing="0.1em">
                {label}
              </Text>
              <Text fontSize="22px" fontWeight="800" color={isHealthy ? accent : unhealthyColor} lineHeight="1">
                {value ?? '—'}
              </Text>
              {helpText && (
                <Text fontSize="9px" color={lbl} letterSpacing="0.06em">
                  {helpText}
                </Text>
              )}
            </VStack>
            {Ic && (
              <Box p={2} bg={iconBg} borderRadius="lg" flexShrink={0}>
                <Icon as={Ic} color={accent} boxSize={4} />
              </Box>
            )}
          </Flex>
        )}
      </Box>
    </MotionBox>
  );
});

const LiveDot = memo(({ isLive = true }) => (
  <Box
    w="9px" h="9px" borderRadius="full" flexShrink={0}
    bg={isLive ? '#34d399' : 'gray.400'}
    style={{
      boxShadow:  isLive ? '0 0 8px #34d399' : 'none',
      animation:  isLive ? `${livePulseKF} 1.8s infinite` : 'none',
    }}
  />
));

const ProductionModelSkeleton = () => {
  const bd = useColorModeValue('gray.100', 'rgba(255,255,255,0.06)');
  return (
    <VStack spacing={5} align="stretch">
      <Flex justify="space-between" align="center">
        <Skeleton height="28px" w="140px" borderRadius="full" />
        <Skeleton height="22px" w="80px" borderRadius="md" />
      </Flex>
      <SimpleGrid columns={{ base: 1, sm: 2, '2xl': 4 }} spacing={4}>
        {[...Array(4)].map((_, i) => (
          <Box key={i} p={4} borderWidth="1px" borderColor={bd} borderRadius="xl">
            <VStack spacing={2} align="flex-start">
              <Skeleton height="10px" w="70%" />
              <Skeleton height="24px" w="50%" />
              <Skeleton height="8px"  w="40%" />
            </VStack>
          </Box>
        ))}
      </SimpleGrid>
      <Box borderWidth="1px" borderColor={bd} borderRadius="xl" p={4}>
        <Skeleton height="14px" w="200px" mb={4} />
        <Skeleton height="260px" borderRadius="lg" />
      </Box>
    </VStack>
  );
};

const StagedModelSkeleton = () => {
  const bd = useColorModeValue('gray.100', 'rgba(255,255,255,0.06)');
  return (
    <VStack spacing={3} align="stretch">
      {[...Array(3)].map((_, i) => (
        <Box key={i} p={4} borderWidth="1px" borderColor={bd} borderRadius="xl">
          <Flex justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={2} flex={1}>
              <Skeleton height="20px" w="120px" />
              <SimpleGrid columns={2} spacing={2} w="full">
                <Skeleton height="30px" borderRadius="md" />
                <Skeleton height="30px" borderRadius="md" />
              </SimpleGrid>
              <Skeleton height="10px" w="160px" />
            </VStack>
            <VStack spacing={2} w="160px" ml={4}>
              <Skeleton height="32px" w="full" borderRadius="md" />
              <Skeleton height="28px" w="full" borderRadius="md" />
            </VStack>
          </Flex>
        </Box>
      ))}
    </VStack>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 5 — MODEL COMPARISON MODAL
// ─────────────────────────────────────────────────────────────────────────────

const ModelComparisonModal = ({ isOpen, onClose, productionModel, stagedModel }) => {
  const headerBg   = useColorModeValue('gray.50', 'gray.700');
  const rowHoverBg = useColorModeValue('gray.50', 'rgba(255,255,255,0.03)');
  const bd         = useColorModeValue('gray.200', 'rgba(255,255,255,0.07)');
  const muted      = useColorModeValue('gray.500', 'gray.400');
  
  const headerGradient = useColorModeValue(
    'linear-gradient(135deg, rgba(49,130,206,0.08), rgba(56,178,172,0.06))',
    'linear-gradient(135deg, rgba(49,130,206,0.12), rgba(56,178,172,0.08))'
  );
  const prodBadgeBg = useColorModeValue('green.50','rgba(52,211,153,0.08)');
  const prodBadgeBorder = useColorModeValue('green.200','rgba(52,211,153,0.2)');
  const stagedBadgeBg = useColorModeValue('purple.50','rgba(167,139,250,0.08)');
  const stagedBadgeBorder = useColorModeValue('purple.200','rgba(167,139,250,0.2)');

  const metrics    = ['mae', 'r2', 'rmse', 'mape'];
  const getMetricValue = (model, metric) => extractMetric(model, metric);

  const metricLabels = { mae: 'MAE', r2: 'R² / C-IDX', rmse: 'RMSE', mape: 'MAPE' };
  const metricIcons = { mae: FaTachometerAlt, r2: MdTrendingUp, rmse: FaDatabase, mape: FaChartBar };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" motionPreset="slideInBottom">
      <ModalOverlay backdropFilter="blur(4px)" bg="rgba(0,0,0,0.5)" />
      <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="0 0 40px rgba(49,130,206,0.2)">
        <ModalHeader bg={headerGradient} borderBottomWidth="1px" borderColor={bd} pb={4}>
          <HStack spacing={3}>
            <Icon as={FaChartBar} color="blue.400" boxSize={5} />
            <VStack spacing={0} align="flex-start">
              <Text fontSize="15px" fontWeight="800">Model Performance Comparison</Text>
              <Text fontSize="10px" color={muted} letterSpacing="0.08em">PRODUCTION vs STAGED</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          <MotionFlex variants={STAGGER_CONTAINER} initial="hidden" animate="visible" direction="column" gap={4}>
            <MotionBox variants={FADE_UP}>
              <Flex gap={3} mb={2} wrap="wrap">
                <Flex align="center" gap={2} px={3} py={1.5} borderRadius="lg" bg={prodBadgeBg} borderWidth="1px" borderColor={prodBadgeBorder}>
                  <LiveDot isLive />
                  <Text fontSize="10px" fontWeight="700" color="green.500">PRODUCTION · v{productionModel?.model_version ?? '—'}</Text>
                </Flex>
                <Flex align="center" gap={2} px={3} py={1.5} borderRadius="lg" bg={stagedBadgeBg} borderWidth="1px" borderColor={stagedBadgeBorder}>
                  <Box w="9px" h="9px" borderRadius="full" bg="purple.400" />
                  <Text fontSize="10px" fontWeight="700" color="purple.400">STAGED · v{stagedModel?.model_version ?? '—'}</Text>
                </Flex>
              </Flex>
            </MotionBox>

            <MotionBox variants={FADE_UP}>
              <TableContainer borderRadius="xl" borderWidth="1px" borderColor={bd} overflow="hidden">
                <Table variant="simple" size="sm">
                  <Thead bg={headerBg}>
                    <Tr>
                      {['METRIC','PRODUCTION','STAGED','DELTA'].map(h => (
                        <Th key={h} fontSize="9px" letterSpacing="0.12em" color={muted}>{h}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {metrics.map((metric) => {
                      const prodVal   = getMetricValue(productionModel, metric);
                      const stagedVal = getMetricValue(stagedModel, metric);
                      const color     = metricDeltaColor(metric, prodVal === 'N/A' ? null : prodVal, stagedVal === 'N/A' ? null : stagedVal);
                      const ProdIcon = metricIcons[metric];
                      return (
                        <Tr key={metric} _hover={{ bg: rowHoverBg }} transition="background 0.15s">
                          <Td>
                            <HStack spacing={2}>
                              {ProdIcon && <Icon as={ProdIcon} color="blue.400" boxSize={3} />}
                              <Text fontWeight="800" textTransform="uppercase" fontSize="11px">{metricLabels[metric] ?? metric}</Text>
                            </HStack>
                          </Td>
                          <Td fontSize="11px" fontWeight="600">{typeof prodVal === 'number' ? prodVal.toFixed(3) : prodVal}</Td>
                          <Td fontSize="11px" fontWeight="600">{typeof stagedVal === 'number' ? stagedVal.toFixed(3) : stagedVal}</Td>
                          <Td>
                            <Badge colorScheme={color} borderRadius="full" px={2} py={0.5} fontSize="10px" fontWeight="700">
                              {color === 'green' ? '↑ Better' : color === 'red' ? '↓ Worse' : '—'}
                            </Badge>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </MotionBox>
          </MotionFlex>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={bd}>
          <Button onClick={onClose} fontSize="11px" letterSpacing="0.06em">CLOSE</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 6 — DELETE MODEL DIALOG
// ─────────────────────────────────────────────────────────────────────────────

const DeleteModelDialog = ({ isOpen, onClose, onConfirm, modelVersion, isDeleting }) => {
  const cancelRef = useRef();
  
  const bd        = useColorModeValue('gray.200', 'rgba(255,255,255,0.07)');
  const redBorder = useColorModeValue('red.200','rgba(239,68,68,0.2)');
  const redText   = useColorModeValue('red.600','red.300');

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} motionPreset="slideInBottom">
      <AlertDialogOverlay backdropFilter="blur(3px)" bg="rgba(0,0,0,0.45)">
        <AlertDialogContent borderRadius="2xl" borderWidth="1px" borderColor={redBorder} boxShadow="0 0 30px rgba(239,68,68,0.15)">
          <AlertDialogHeader fontSize="15px" fontWeight="800" borderBottomWidth="1px" borderColor={bd} pb={3}>
            <HStack spacing={2}>
              <Icon as={FaExclamationTriangle} color="red.400" boxSize={4} />
              <Text>Delete Staged Model</Text>
            </HStack>
          </AlertDialogHeader>
          <AlertDialogBody py={5} fontSize="13px" lineHeight="1.7">
            Are you sure you want to delete model <Code colorScheme="red" fontSize="12px" px={2} py={0.5} borderRadius="md">{modelVersion}</Code>?{' '}
            <Text as="span" color={redText} fontWeight="700">This action cannot be undone.</Text>
          </AlertDialogBody>
          <AlertDialogFooter gap={3} borderTopWidth="1px" borderColor={bd}>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isDeleting} fontSize="10px" letterSpacing="0.06em">CANCEL</Button>
            <Button colorScheme="red" onClick={onConfirm} isLoading={isDeleting} loadingText="DELETING..." fontSize="10px" letterSpacing="0.06em">
              DELETE MODEL
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 7 — MAIN WIDGET
// ─────────────────────────────────────────────────────────────────────────────

const RulModelInsightsWidget = ({ dateRange }) => {

  const [insights,            setInsights]            = useState(null);
  const [stagedModels,        setStagedModels]        = useState([]);
  const [isLoading,           setIsLoading]           = useState(true);
  const [isLoadingStaged,     setIsLoadingStaged]     = useState(true);
  const [isPromoting,         setIsPromoting]         = useState(null);
  const [error,               setError]               = useState(null);
  const [errorStaged,         setErrorStaged]         = useState(null);
  const [isRetraining,        setIsRetraining]        = useState(false);
  const [retrainingProgress,  setRetrainingProgress]  = useState(0);
  const [autoRefresh,         setAutoRefresh]         = useState(false);
  const [sortBy,              setSortBy]              = useState('date');
  const [filterStatus,        setFilterStatus]        = useState('all');
  const [selectedComparison,  setSelectedComparison]  = useState(null);
  const [modelToDelete,       setModelToDelete]       = useState(null);
  const [isDeleting,          setIsDeleting]          = useState(false);
  const [trainingLogs,        setTrainingLogs]        = useState([]);

  const toast = useToast();
  const { isOpen: isComparisonOpen, onOpen: onComparisonOpen, onClose: onComparisonClose } = useDisclosure();
  const { isOpen: isDeleteOpen,     onOpen: onDeleteOpen,     onClose: onDeleteClose }     = useDisclosure();
  const { isOpen: isDetailsOpen,    onToggle: onToggleDetails }                            = useDisclosure({ defaultIsOpen: true });

  const chartTextColor = useColorModeValue('gray.800', 'white');
  const listItemBg     = useColorModeValue('gray.50',  'gray.700');
  const cardBg         = useColorModeValue('white',    'gray.800');
  const borderColor    = useColorModeValue('gray.200', 'gray.600');
  const statBg         = useColorModeValue('blue.50',  'blue.900');
  const progressBg     = useColorModeValue('blue.50',  'blue.900');
  const hoverBorder    = useColorModeValue('blue.400', 'blue.300');
  const gridBorderColor = useColorModeValue('#e2e8f0', '#2d3748');
  const mutedText      = useColorModeValue('gray.500', 'gray.400');
  
  const panelHeaderBg  = useColorModeValue('linear-gradient(135deg,rgba(49,130,206,0.04),rgba(56,178,172,0.04))', 'linear-gradient(135deg,rgba(49,130,206,0.07),rgba(56,178,172,0.07))');
  const tabActiveBg    = useColorModeValue('white', 'rgba(255,255,255,0.08)');
  const tabBg          = useColorModeValue('gray.100', 'rgba(255,255,255,0.04)');
  const stagedMetricBg = useColorModeValue('white', 'rgba(255,255,255,0.03)');
  const logSuccessBorder = useColorModeValue('green.200', 'rgba(52,211,153,0.2)');
  const logErrorBorder   = useColorModeValue('red.200', 'rgba(239,68,68,0.2)');
  const headerIconBg     = useColorModeValue('blue.50', 'rgba(49,130,206,0.1)');
  const headerIconBorder = useColorModeValue('blue.200', 'rgba(49,130,206,0.2)');
  const titleGradient    = useColorModeValue('linear(to-r, blue.600, teal.500)', 'linear(to-r, #22d3ee, #34d399)');
  const iconButtonHoverBg= useColorModeValue('blue.50', 'rgba(49,130,206,0.1)');
  const progressBorderColor = useColorModeValue('blue.200', 'rgba(49,130,206,0.25)');

  const safeParseJson = useCallback((data, fallback = {}) => {
    if (typeof data === 'object' && data !== null) return data;
    if (typeof data === 'string') {
      try { return typeof JSON.parse(data) === 'object' ? JSON.parse(data) : fallback; } 
      catch (e) { return fallback; }
    }
    return fallback;
  }, []);

  const fetchInsights = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await analyticsService.getRulModelInsights();
      const rawInsights = response?.data || response;
      if (rawInsights && typeof rawInsights === 'object') {
        setInsights({
          ...rawInsights,
          performance_metrics: safeParseJson(rawInsights.performance_metrics),
          feature_importance:  safeParseJson(rawInsights.feature_importance),
        });
      } else {
        const msg = rawInsights?.error || rawInsights?.message || 'Invalid data received';
        if (msg.includes('No trained RUL models') || msg.includes('No active RUL model')) setInsights(null);
        else throw new Error(msg);
      }
    } catch (err) {
      setError(err.message || 'Could not load production model insights.');
      setInsights(null);
    } finally {
      setIsLoading(false);
    }
  }, [safeParseJson]);

  const fetchStagedModels = useCallback(async () => {
    setIsLoadingStaged(true);
    setErrorStaged(null);
    try {
      const response = await aiService.getModels({ modelType: 'RUL_PREDICTION', status: 'staging' });
      const models = response?.data || response || [];
      if (Array.isArray(models)) {
        setStagedModels(models.map((m) => ({
          ...m,
          performance_metrics: safeParseJson(m.performance_metrics),
          feature_importance:  safeParseJson(m.feature_importance),
        })));
      } else {
        throw new Error('Invalid staged models data');
      }
    } catch (err) {
      setErrorStaged(err.message || 'Could not load staged models.');
      setStagedModels([]);
    } finally {
      setIsLoadingStaged(false);
    }
  }, [safeParseJson]);

  useEffect(() => {
    fetchInsights();
    fetchStagedModels();
  }, [fetchInsights, fetchStagedModels]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchInsights();
      fetchStagedModels();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchInsights, fetchStagedModels]);

  useEffect(() => {
    if (!isRetraining) { setRetrainingProgress(0); return; }
    const interval = setInterval(() => {
      setRetrainingProgress(prev => (prev >= 95 ? prev : prev + Math.random() * 5));
    }, 1000);
    return () => clearInterval(interval);
  }, [isRetraining]);

  const handleRetrain = async () => {
    setIsRetraining(true);
    setRetrainingProgress(0);
    toast({ title: 'Retraining Initiated', description: 'Training RUL model...', status: 'info', duration: 5000, isClosable: true });
    try {
      const response = await triggerRulModelRetraining();
      setRetrainingProgress(100);
      toast({ title: 'Retraining Complete', description: response.message || 'Model successfully retrained.', status: 'success', duration: 7000, isClosable: true });
      setTrainingLogs(prev => [{ timestamp: new Date(), status: 'success', message: 'Model retrained successfully' }, ...prev].slice(0, 10));
      setTimeout(() => { fetchInsights(); fetchStagedModels(); }, 2000);
    } catch (err) {
      toast({ title: 'Retraining Failed', description: err.message || 'An error occurred.', status: 'error', duration: 7000, isClosable: true });
      setTrainingLogs(prev => [{ timestamp: new Date(), status: 'error', message: err.message }, ...prev].slice(0, 10));
    } finally {
      setIsRetraining(false);
      setRetrainingProgress(0);
    }
  };

  const handlePromote = async (modelVersion) => {
    setIsPromoting(modelVersion);
    toast({ title: 'Promoting Model', description: `Moving version ${modelVersion}...`, status: 'info', duration: 3000, isClosable: true });
    try {
      const response = await aiService.promoteModel('RUL_PREDICTION', modelVersion);
      toast({ title: 'Promotion Successful', description: response.message || `Model ${modelVersion} is now live.`, status: 'success', duration: 5000, isClosable: true });
      fetchInsights();
      fetchStagedModels();
    } catch (err) {
      toast({ title: 'Promotion Failed', description: err.message, status: 'error', duration: 7000, isClosable: true });
    } finally {
      setIsPromoting(null);
    }
  };

  const handleDeleteModel = async () => {
    if (!modelToDelete) return;
    setIsDeleting(true);
    try {
      await aiService.deleteModel('RUL_PREDICTION', modelToDelete);
      toast({ title: 'Model Deleted', description: `Model ${modelToDelete} removed.`, status: 'success', duration: 5000 });
      fetchStagedModels();
      onDeleteClose();
    } catch (err) {
      toast({ title: 'Deletion Failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setIsDeleting(false);
      setModelToDelete(null);
    }
  };

  const handleCompare = (stagedModel) => {
    setSelectedComparison(stagedModel);
    onComparisonOpen();
  };

  const handleExport = useCallback(() => {
    const exportData = { timestamp: new Date().toISOString(), production: insights, staged: stagedModels, trainingLogs };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `rul-models-${new Date().toISOString()}.json`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    toast({ title: 'Export Complete', description: 'Data exported.', status: 'success', duration: 3000 });
  }, [insights, stagedModels, trainingLogs, toast]);

  const filteredStagedModels = useMemo(() => {
    let models = [...stagedModels];
    if (sortBy === 'date') models.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else if (sortBy === 'performance') {
      models.sort((a, b) => {
        const aPM = a.performance_metrics || {}; const bPM = b.performance_metrics || {};
        const aMAE = aPM.mae !== undefined ? aPM.mae : (aPM.mean_absolute_error !== undefined ? aPM.mean_absolute_error : (a.mean_absolute_error !== undefined ? a.mean_absolute_error : Infinity));
        const bMAE = bPM.mae !== undefined ? bPM.mae : (bPM.mean_absolute_error !== undefined ? bPM.mean_absolute_error : (b.mean_absolute_error !== undefined ? b.mean_absolute_error : Infinity));
        return aMAE - bMAE;
      });
    }
    return models.slice(0, 10);
  }, [stagedModels, sortBy]);

  const renderProductionContent = () => {
    if (isLoading) return <ProductionModelSkeleton />;
    if (error) {
      const noModel = error.includes('No trained RUL models') || error.includes('No active RUL model');
      return (
        <MotionBox variants={FADE_UP} initial="hidden" animate="visible">
          <Alert status={noModel ? 'info' : 'error'} borderRadius="xl" variant="left-accent">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="12px" letterSpacing="0.08em">{noModel ? 'NO ACTIVE PRODUCTION MODEL' : 'ERROR LOADING MODEL'}</AlertTitle>
              <AlertDescription fontSize="11px">{noModel ? 'Retrain a model or promote one from staging below.' : error}</AlertDescription>
            </Box>
          </Alert>
        </MotionBox>
      );
    }
    if (!insights || !insights.model_version) {
      return (
        <MotionBox variants={FADE_UP} initial="hidden" animate="visible">
          <Alert status="warning" borderRadius="xl" variant="subtle"><AlertIcon />Could not load production model details. Please refresh.</Alert>
        </MotionBox>
      );
    }

    const pm = insights.performance_metrics || {};
    let maeVal = 'N/A';
    if (pm.mae !== undefined) maeVal = pm.mae; else if (pm.mean_absolute_error !== undefined) maeVal = pm.mean_absolute_error; else if (insights.mean_absolute_error !== undefined) maeVal = insights.mean_absolute_error;
    const mae = typeof maeVal === 'number' ? maeVal : (maeVal !== 'N/A' ? parseFloat(maeVal) : null);

    let r2Val = 'N/A';
    if (pm.r2 !== undefined) r2Val = pm.r2; else if (pm.r_squared !== undefined) r2Val = pm.r_squared; else if (pm.r2_score !== undefined) r2Val = pm.r2_score; else if (pm.c_index !== undefined) r2Val = pm.c_index; else if (insights.r2_score !== undefined) r2Val = insights.r2_score; else if (insights.c_index !== undefined) r2Val = insights.c_index;
    const r2 = typeof r2Val === 'number' ? r2Val : (r2Val !== 'N/A' ? parseFloat(r2Val) : null);

    let rmseVal = 'N/A';
    if (pm.rmse !== undefined) rmseVal = pm.rmse; else if (pm.root_mean_squared_error !== undefined) rmseVal = pm.root_mean_squared_error; else if (insights.root_mean_squared_error !== undefined) rmseVal = insights.root_mean_squared_error;
    const rmse = typeof rmseVal === 'number' ? rmseVal : (rmseVal !== 'N/A' ? parseFloat(rmseVal) : null);

    const fi = insights.feature_importance || {};
    const chartCategories = Object.keys(fi).map(k => k.replace(/_/g, ' ').toUpperCase());
    const chartSeriesData = Object.values(fi).map(v => typeof v === 'number' ? v * 100 : 0);

    const featureImportanceOptions = {
      chart: { type: 'bar', toolbar: { show: false }, foreColor: chartTextColor, animations: { enabled: true, dynamicAnimation: { speed: 500 } } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true, dataLabels: { position: 'top' } } },
      xaxis: { categories: chartCategories, title: { text: 'Importance Score (%)' }, labels: { formatter: (val) => val ? `${val.toFixed(0)}%` : '0%' }, axisBorder: { show: false } },
      yaxis: { labels: { maxWidth: 160, style: { fontWeight: 600, fontSize: '11px' } } },
      tooltip: { theme: 'dark', y: { formatter: (val) => val ? `${val.toFixed(2)}% influence` : '0%' } },
      legend: { show: false }, dataLabels: { enabled: false },
      colors: ['#3182CE', '#805AD5', '#38B2AC', '#DD6B20', '#E53E3E', '#4299E1'],
      grid: { borderColor: gridBorderColor, strokeDashArray: 4 }
    };
    
    const featureImportanceSeries = [{ name: 'Importance', data: chartSeriesData }];

    const isR2Healthy   = r2   !== null && r2   > 0.7;
    const isMaeHealthy  = mae  !== null && mae  < 10;
    const isRmseHealthy = rmse !== null && rmse < 15;

    return (
      <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
        <VStack spacing={6} align="stretch">
          <MotionBox variants={FADE_UP}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
              <HStack spacing={3} wrap="wrap">
                <LiveDot isLive />
                <Tag colorScheme="green" size="lg" variant="solid" fontSize="11px" letterSpacing="0.08em">
                  <CheckCircleIcon mr={2} /> PRODUCTION
                </Tag>
                <Badge colorScheme="blue" fontSize="sm">v{insights.model_version}</Badge>
              </HStack>
              <HStack spacing={2}>
                <Tooltip label="Model Health: Excellent" hasArrow>
                  <Badge colorScheme="green" p={2} borderRadius="lg" fontSize="10px">
                    <Icon as={FaCheckCircle} mr={1} /> HEALTHY
                  </Badge>
                </Tooltip>
              </HStack>
            </Flex>
          </MotionBox>

          <SimpleGrid columns={{ base: 1, sm: 2, '2xl': 4 }} spacing={4}>
            <MetricCard label="Model Version" value={insights.model_version} helpText={insights.activated_at ? new Date(insights.activated_at).toLocaleDateString() : 'N/A'} accent="#3182CE" icon={FaShieldAlt} />
            <MetricCard label="Mean Absolute Error" value={mae !== null ? mae.toFixed(2) : 'N/A'} helpText="days accuracy" accent={isMaeHealthy ? '#38B2AC' : '#E53E3E'} icon={FaTachometerAlt} isHealthy={isMaeHealthy} />
            <MetricCard label="R² / C-Index" value={r2 !== null ? r2.toFixed(3) : 'N/A'} helpText="goodness of fit" accent={isR2Healthy ? '#38B2AC' : '#E53E3E'} icon={MdTrendingUp} isHealthy={isR2Healthy} />
            <MetricCard label="RMSE" value={rmse !== null ? rmse.toFixed(2) : 'N/A'} helpText="prediction error" accent={isRmseHealthy ? '#38B2AC' : '#E53E3E'} icon={FaDatabase} isHealthy={isRmseHealthy} />
          </SimpleGrid>

          <Collapse in={isDetailsOpen} animateOpacity>
            <MotionBox variants={FADE_UP}>
              <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5} bg={cardBg} boxShadow="sm">
                <HStack mb={4} spacing={2}>
                  <Icon as={FaChartBar} color="blue.400" boxSize={4} />
                  <Heading size="sm" fontWeight="800">Feature Importance Analysis</Heading>
                </HStack>
                {chartCategories.length > 0 ? (
                  <Box w="100%" overflowX="auto">
                     <ReactApexChart options={featureImportanceOptions} series={featureImportanceSeries} type="bar" height={280} />
                  </Box>
                ) : (
                  <Flex direction="column" align="center" py={10} gap={2}>
                    <Icon as={FaChartBar} boxSize={8} color={mutedText} opacity={0.3} />
                    <Text color={mutedText} fontSize="11px" letterSpacing="0.08em">NO FEATURE IMPORTANCE DATA AVAILABLE</Text>
                  </Flex>
                )}
                <Text fontSize="10px" color={mutedText} mt={3} textAlign="center" letterSpacing="0.06em">
                  Higher scores indicate features with greater predictive power
                </Text>
              </Box>
            </MotionBox>
          </Collapse>
        </VStack>
      </motion.div>
    );
  };

  const renderStagedContent = () => {
    if (isLoadingStaged) return <StagedModelSkeleton />;
    if (errorStaged) {
      return (
        <MotionBox variants={FADE_UP} initial="hidden" animate="visible">
          <Alert status="error" borderRadius="xl" variant="left-accent"><AlertIcon />{errorStaged}</Alert>
        </MotionBox>
      );
    }
    if (!filteredStagedModels || filteredStagedModels.length === 0) {
      return (
        <MotionBox variants={FADE_UP} initial="hidden" animate="visible">
          <Alert status="info" borderRadius="xl" variant="subtle">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="11px" letterSpacing="0.08em">NO STAGED MODELS</AlertTitle>
              <AlertDescription fontSize="11px">Retrain to create a new challenger model for testing.</AlertDescription>
            </Box>
          </Alert>
        </MotionBox>
      );
    }
    return (
      <>
        <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
          <HStack><Badge colorScheme="purple" fontSize="10px">{filteredStagedModels.length} models</Badge></HStack>
          <HStack spacing={2}>
            <Text fontSize="10px" color={mutedText}>SORT BY:</Text>
            <Select size="xs" value={sortBy} onChange={(e) => setSortBy(e.target.value)} w="130px" borderRadius="lg" fontSize="10px">
              <option value="date">Latest First</option>
              <option value="performance">Best MAE</option>
            </Select>
          </HStack>
        </Flex>

        <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
          <List spacing={3}>
            <AnimatePresence>
              {filteredStagedModels.map((model) => {
              const metrics = model.performance_metrics || {};
              const modelMae = parseFloat(metrics.mae !== undefined ? metrics.mae : (metrics.mean_absolute_error !== undefined ? metrics.mean_absolute_error : model.mean_absolute_error));
              const modelR2 = parseFloat(metrics.r2 !== undefined ? metrics.r2 : (metrics.r_squared !== undefined ? metrics.r_squared : (metrics.r2_score !== undefined ? metrics.r2_score : (metrics.c_index !== undefined ? metrics.c_index : (model.r2_score !== undefined ? model.r2_score : model.c_index)))));
                return (
                  <motion.div key={model.model_version} variants={LIST_ITEM_VARIANT} layout>
                    <ListItem p={4} borderWidth={1} borderColor={borderColor} borderRadius="xl" bg={listItemBg} _hover={{ borderColor: 'purple.400', shadow: 'md', transform: 'translateY(-1px)' }} transition="all 0.2s" position="relative" overflow="hidden">
                      <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg="purple.400" style={{ boxShadow: '0 0 8px #805AD5' }} />
                      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
                        <Box flex="1" minW="200px">
                          <HStack mb={2}>
                            <Tag colorScheme="purple" size="sm" fontSize="10px"><ListIcon as={FaHourglassHalf} />STAGING</Tag>
                            <Code fontSize="sm" colorScheme="purple">v{model.model_version}</Code>
                          </HStack>
                          <SimpleGrid columns={2} spacing={2} fontSize="sm" mb={2}>
                            <Box p={2.5} borderRadius="lg" borderWidth="1px" borderColor={borderColor} bg={stagedMetricBg}>
                              <Text color={mutedText} fontSize="9px" letterSpacing="0.08em">MAE</Text>
                              <Text fontWeight="800" fontSize="14px" color={modelMae !== null && modelMae < 10 ? 'teal.400' : 'orange.400'}>{modelMae !== null ? `${modelMae.toFixed(2)} days` : 'N/A'}</Text>
                            </Box>
                            <Box p={2.5} borderRadius="lg" borderWidth="1px" borderColor={borderColor} bg={stagedMetricBg}>
                              <Text color={mutedText} fontSize="9px" letterSpacing="0.08em">R² / C-IDX</Text>
                              <Text fontWeight="800" fontSize="14px" color={modelR2 !== null && modelR2 > 0.7 ? 'teal.400' : 'orange.400'}>{modelR2 !== null ? modelR2.toFixed(3) : 'N/A'}</Text>
                            </Box>
                          </SimpleGrid>
                          <Text fontSize="10px" color={mutedText}>
                            <Icon as={FaClock} mr={1} /> CREATED: {model.created_at ? new Date(model.created_at).toLocaleString() : 'N/A'}
                          </Text>
                        </Box>
                        <VStack spacing={2} align="stretch" minW="200px">
                          <Button size="sm" colorScheme="green" leftIcon={<ArrowUpIcon />} isLoading={isPromoting === model.model_version} loadingText="Promoting..." isDisabled={isPromoting !== null || isRetraining} onClick={() => handlePromote(model.model_version)} fontSize="10px" letterSpacing="0.06em" borderRadius="xl" boxShadow="sm" _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }} transition="all 0.2s">
                            PROMOTE TO PRODUCTION
                          </Button>
                          <HStack>
                            <Button size="xs" variant="outline" leftIcon={<InfoIcon />} onClick={() => handleCompare(model)} isDisabled={!insights} flex="1" fontSize="9px" letterSpacing="0.06em" borderRadius="lg">COMPARE</Button>
                            <Tooltip label="Delete staged model" hasArrow>
                              <IconButton size="xs" variant="outline" colorScheme="red" icon={<DeleteIcon />} onClick={() => { setModelToDelete(model.model_version); onDeleteOpen(); }} aria-label="Delete model" borderRadius="lg" isDisabled={isPromoting !== null} />
                            </Tooltip>
                          </HStack>
                        </VStack>
                      </Flex>
                    </ListItem>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </List>
        </motion.div>
        {stagedModels.length > 10 && (
          <Text fontSize="xs" color={mutedText} mt={3} textAlign="center" letterSpacing="0.06em">
            Showing top 10 models. {stagedModels.length - 10} more available.
          </Text>
        )}
      </>
    );
  };

  return (
    <Card>
      <Box px={0} pb={4} background={panelHeaderBg} borderRadius="xl" mb={2}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Box p={2.5} borderRadius="xl" bg={headerIconBg} borderWidth="1px" borderColor={headerIconBorder}>
              <Icon as={FaRobot} boxSize={5} color="blue.400" />
            </Box>
            <VStack spacing={0} align="flex-start">
              <Heading size="md" fontWeight="800" bgGradient={titleGradient} bgClip="text">
                RUL Model Management
              </Heading>
              <Text fontSize="9px" color={mutedText} letterSpacing="0.1em">PREDICTIVE FAILURE · AI MODEL LIFECYCLE</Text>
            </VStack>
            {autoRefresh && <Badge colorScheme="green" fontSize="9px" letterSpacing="0.08em"><Icon as={RepeatIcon} mr={1} /> AUTO-REFRESH</Badge>}
          </HStack>

          <Flex wrap="wrap" gap={2} align="center" justify="flex-end">
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel htmlFor="auto-refresh" mb="0" fontSize="10px" mr={2} color={mutedText} letterSpacing="0.06em">Auto-refresh</FormLabel>
              <Switch id="auto-refresh" size="sm" isChecked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} colorScheme="teal" />
            </FormControl>
            <Tooltip label="Export All Data" hasArrow>
              <IconButton icon={<DownloadIcon />} onClick={handleExport} size="sm" variant="ghost" aria-label="Export data" borderRadius="lg" _hover={{ bg: iconButtonHoverBg }} />
            </Tooltip>
            <Tooltip label="Refresh Data" hasArrow>
              <IconButton icon={<RepeatIcon />} onClick={() => { fetchInsights(); fetchStagedModels(); }} isLoading={isLoading || isLoadingStaged} size="sm" variant="ghost" aria-label="Refresh" borderRadius="lg" _hover={{ bg: iconButtonHoverBg }} />
            </Tooltip>
            <Button size="sm" colorScheme="blue" leftIcon={<Icon as={FaSync} />} isLoading={isRetraining} loadingText="TRAINING..." isDisabled={isPromoting !== null} onClick={handleRetrain} fontSize="10px" letterSpacing="0.08em" borderRadius="xl" boxShadow="sm" _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }} transition="all 0.2s">
              Retrain Model
            </Button>
          </Flex>
        </Flex>
      </Box>

      <AnimatePresence>
        {isRetraining && (
          <MotionBox variants={FADE_UP} initial="hidden" animate="visible" exit="exit" mb={5} p={4} bg={progressBg} borderRadius="xl" borderWidth="1px" borderColor={progressBorderColor} boxShadow="sm">
            <HStack justify="space-between" mb={3}>
              <HStack spacing={3}>
                <Spinner size="sm" color="blue.400" thickness="3px" />
                <VStack spacing={0} align="flex-start">
                  <Text fontWeight="700" fontSize="12px" letterSpacing="0.06em">TRAINING IN PROGRESS</Text>
                  <Text fontSize="9px" color={mutedText}>This process may take several minutes</Text>
                </VStack>
              </HStack>
              <Text fontSize="18px" fontWeight="800" color="blue.400">{retrainingProgress.toFixed(0)}%</Text>
            </HStack>
            <Progress value={retrainingProgress} size="sm" colorScheme="blue" borderRadius="full" hasStripe isAnimated />
          </MotionBox>
        )}
      </AnimatePresence>

      <Tabs variant="unstyled" colorScheme="blue">
        <TabList bg={tabBg} borderRadius="xl" p={1} mb={5} display="flex" flexWrap="wrap" gap={1}>
          {[
            { label: 'Production Model', icon: FaCheckCircle },
            { label: `Staged Models (${filteredStagedModels.length})`, icon: FaHourglassHalf },
            { label: 'Training Logs', icon: FaHistory },
          ].map((t, i) => (
            <Tab key={t.label} flex="1" borderRadius="lg" py={2} px={3} fontSize="10px" fontWeight="800" letterSpacing="0.06em" color={mutedText} transition="all 0.2s" _selected={{ bg: tabActiveBg, color: 'blue.500', boxShadow: 'sm' }} _hover={{ color: 'blue.400' }}>
              <HStack spacing={2} justify="center">
                <Icon as={t.icon} boxSize={3.5} />
                <Text whiteSpace="nowrap">{t.label}</Text>
              </HStack>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          <TabPanel px={0} pb={0}>
            <VStack align="stretch" spacing={4}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                <Text fontSize="10px" color={mutedText} letterSpacing="0.08em">ACTIVE MODEL SERVING PREDICTIONS IN PRODUCTION</Text>
                <Button size="xs" variant="ghost" rightIcon={isDetailsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />} onClick={onToggleDetails} fontSize="9px" letterSpacing="0.06em" color={mutedText}>
                  {isDetailsOpen ? 'HIDE' : 'SHOW'} DETAILS
                </Button>
              </Flex>
              {renderProductionContent()}
            </VStack>
          </TabPanel>

          <TabPanel px={0} pb={0}>
            <VStack align="stretch" spacing={4}>
              <MotionBox variants={FADE_UP} initial="hidden" animate="visible">
                <Alert status="info" variant="left-accent" borderRadius="xl">
                  <AlertIcon />
                  <Box fontSize="sm">
                    <AlertTitle fontSize="11px" letterSpacing="0.08em">CHALLENGER MODELS</AlertTitle>
                    <AlertDescription fontSize="11px">Test and compare models before promoting to production.</AlertDescription>
                  </Box>
                </Alert>
              </MotionBox>
              {renderStagedContent()}
            </VStack>
          </TabPanel>

          <TabPanel px={0} pb={0}>
            <VStack align="stretch" spacing={3}>
              <AnimatePresence mode="popLayout">
                {trainingLogs.length > 0 ? (
                  trainingLogs.map((log, idx) => (
                    <MotionBox key={idx} variants={LIST_ITEM_VARIANT} initial="hidden" animate="visible" exit="exit">
                      <Box p={3} borderWidth="1px" borderColor={log.status === 'success' ? logSuccessBorder : logErrorBorder} borderRadius="xl" bg={listItemBg} position="relative" overflow="hidden">
                        <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={log.status === 'success' ? '#34d399' : '#ef4444'} style={{ boxShadow: `0 0 6px ${log.status === 'success' ? '#34d399' : '#ef4444'}` }} />
                        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                          <HStack spacing={3}>
                            <Icon as={log.status === 'success' ? FaCheckCircle : FaExclamationTriangle} color={log.status === 'success' ? 'green.400' : 'red.400'} boxSize={3.5} />
                            <VStack spacing={0} align="flex-start">
                              <Text fontSize="11px" fontWeight="700">{log.message}</Text>
                              <Text fontSize="9px" color={mutedText}>{log.status.toUpperCase()}</Text>
                            </VStack>
                          </HStack>
                          <Text fontSize="9px" color={mutedText}>{log.timestamp.toLocaleString()}</Text>
                        </Flex>
                      </Box>
                    </MotionBox>
                  ))
                ) : (
                  <MotionBox variants={FADE_UP} initial="hidden" animate="visible">
                    <Alert status="info" borderRadius="xl"><AlertIcon /><Text fontSize="11px" letterSpacing="0.06em">No training logs yet. Logs appear after training runs.</Text></Alert>
                  </MotionBox>
                )}
              </AnimatePresence>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <ModelComparisonModal isOpen={isComparisonOpen} onClose={onComparisonClose} productionModel={insights} stagedModel={selectedComparison} />
      <DeleteModelDialog isOpen={isDeleteOpen} onClose={onDeleteClose} onConfirm={handleDeleteModel} modelVersion={modelToDelete} isDeleting={isDeleting} />
    </Card>
  );
};

export default memo(RulModelInsightsWidget);